// === Admin: CECs ===
// CEC parameter reference editor.
// Select a CEC number → edit 18 soil-test parameters across Desired Min / Desired rows.
// Ported from old Click2Gro (app/Controllers/Admin/CecsController.php).

import { useEffect, useState, useCallback } from 'react';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
  TextField,
  CircularProgress,
  Alert,
  Snackbar,
  MenuItem,
  Select,
  InputLabel,
  FormControl,
  Tooltip,
  IconButton,
} from '@mui/material';
import { Save as SaveIcon, ArrowBack as BackIcon } from '@mui/icons-material';
import { alpha } from '@mui/material/styles';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../lib/auth';
import { AppTableHead, AppTableHeadCell, AppTableRow } from '../components/AppTable';

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const API_BASE = '/api/v1/admin/cecs';

/** All 18 CEC parameters in display order */
const PARAMETERS: { key: string; label: string; integer?: boolean }[] = [
  { key: 'totalOrganicCarbon', label: 'Total Organic Carbon' },
  { key: 'ECEC',               label: 'ECEC' },
  { key: 'pHwater',            label: 'pH 1:5 water' },
  { key: 'colwellphosphorus',  label: 'Colwell Phosphorus', integer: true },
  { key: 'nitrogen',           label: 'Nitrogen (N)',        integer: true },
  { key: 'mcpsulfur',          label: 'MCP Sulfur (S)',      integer: true },
  { key: 'calcium',            label: 'Calcium % (CA)' },
  { key: 'magnesium',          label: 'Magnesium % (Mg)' },
  { key: 'potassium',          label: 'Potassium % (K)' },
  { key: 'sodium',             label: 'Sodium %' },
  { key: 'aluminium',          label: 'Aluminium %' },
  { key: 'hydrogen',           label: 'Hydrogen %' },
  { key: 'boron',              label: 'Boron' },
  { key: 'iron',               label: 'Iron (Fe)',           integer: true },
  { key: 'manganese',          label: 'Manganese (Mn)' },
  { key: 'copper',             label: 'Copper (Cu)' },
  { key: 'zinc',               label: 'Zinc (Zn)' },
  { key: 'salinity',           label: 'Salinity EC 1:5' },
];

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface CecNumberItem {
  id: number;
  cec_number: string;
}

type ParamValues = Record<string, string>; // keyed by PARAMETERS[n].key, string for input

interface CecData {
  cecNumber: string;
  desired: Record<string, number | null> | null;
  desiredMin: Record<string, number | null> | null;
}

// ---------------------------------------------------------------------------
// Helper: convert a raw cec row to ParamValues (string map for inputs)
// ---------------------------------------------------------------------------
function toParamValues(row: Record<string, number | null> | null): ParamValues {
  const out: ParamValues = {};
  for (const p of PARAMETERS) {
    const v = row?.[p.key];
    out[p.key] = v === null || v === undefined ? '' : String(v);
  }
  return out;
}

// ---------------------------------------------------------------------------
// CecsPage
// ---------------------------------------------------------------------------

export function CecsPage() {
  const { session } = useAuth();
  const navigate = useNavigate();

  // CEC number dropdown
  const [cecNumbers, setCecNumbers]     = useState<CecNumberItem[]>([]);
  const [selectedId, setSelectedId]     = useState<number | ''>('');
  const [loadingList, setLoadingList]   = useState(true);

  // Parameter data
  const [cecData, setCecData]           = useState<CecData | null>(null);
  const [loadingData, setLoadingData]   = useState(false);

  // Editable form state
  const [desired,    setDesired]        = useState<ParamValues>({});
  const [desiredMin, setDesiredMin]     = useState<ParamValues>({});

  // UI state
  const [saving, setSaving]             = useState(false);
  const [snack, setSnack]               = useState<{ open: boolean; msg: string; severity: 'success' | 'error' }>({
    open: false, msg: '', severity: 'success',
  });

  const headers = { Authorization: `Bearer ${session?.access_token}` };

  // -------------------------------------------------------------------------
  // Load CEC number list on mount
  // -------------------------------------------------------------------------
  useEffect(() => {
    setLoadingList(true);
    fetch(API_BASE, { headers })
      .then((r) => r.json())
      .then((json: { data: CecNumberItem[] }) => {
        setCecNumbers(json.data ?? []);
      })
      .catch(() => setSnack({ open: true, msg: 'Failed to load CEC numbers', severity: 'error' }))
      .finally(() => setLoadingList(false));
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [session]);

  // -------------------------------------------------------------------------
  // Load CEC data when selection changes
  // -------------------------------------------------------------------------
  const loadCecData = useCallback(
    (id: number) => {
      setLoadingData(true);
      setCecData(null);
      fetch(`${API_BASE}/${id}`, { headers })
        .then((r) => {
          if (!r.ok) throw new Error('Not found');
          return r.json();
        })
        .then((json: CecData) => {
          setCecData(json);
          setDesired(toParamValues(json.desired));
          setDesiredMin(toParamValues(json.desiredMin));
        })
        .catch(() => setSnack({ open: true, msg: 'Failed to load CEC data', severity: 'error' }))
        .finally(() => setLoadingData(false));
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [session],
  );

  const handleSelectChange = (id: number) => {
    setSelectedId(id);
    loadCecData(id);
  };

  // -------------------------------------------------------------------------
  // Save
  // -------------------------------------------------------------------------
  const handleSave = async () => {
    if (!selectedId) return;
    setSaving(true);

    // Convert string inputs back to numbers (null for empty)
    function toNumbers(vals: ParamValues): Record<string, number | null> {
      const out: Record<string, number | null> = {};
      for (const p of PARAMETERS) {
        const s = vals[p.key]?.trim();
        out[p.key] = s === '' || s === undefined ? null : Number(s);
      }
      return out;
    }

    try {
      const res = await fetch(`${API_BASE}/${selectedId}`, {
        method: 'PUT',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ desired: toNumbers(desired), desiredMin: toNumbers(desiredMin) }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error((err as { error?: string }).error ?? 'Save failed');
      }
      setSnack({ open: true, msg: `${cecData?.cecNumber ?? 'CEC'} saved successfully`, severity: 'success' });
    } catch (e) {
      setSnack({ open: true, msg: e instanceof Error ? e.message : 'Save failed', severity: 'error' });
    } finally {
      setSaving(false);
    }
  };

  // -------------------------------------------------------------------------
  // Render
  // -------------------------------------------------------------------------
  return (
    <>
      <Container maxWidth="xl" sx={{ py: 4 }}>

        {/* ── Header ── */}
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 3, gap: 2 }}>
          <Tooltip title="Back to Dashboard">
            <IconButton onClick={() => navigate('/dashboard')} size="small">
              <BackIcon />
            </IconButton>
          </Tooltip>
          <Box sx={{ flex: 1 }}>
            <Typography variant="h5" fontWeight={700}>CECs</Typography>
            <Typography variant="body2" color="text.secondary">
              Cation exchange capacity parameters
            </Typography>
          </Box>
        </Box>

        {/* ── Toolbar ── */}
        <Paper
          elevation={0}
          sx={(theme) => ({
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap',
            gap: 2,
            px: 2,
            py: 1.5,
            mb: 2,
            border: `1px solid ${theme.palette.divider}`,
            borderRadius: 2,
          })}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
            {/* CEC number selector */}
            <FormControl size="small" sx={{ minWidth: 200 }}>
              <InputLabel id="cec-select-label">CEC Number</InputLabel>
              <Select
                labelId="cec-select-label"
                label="CEC Number"
                value={selectedId}
                onChange={(e) => handleSelectChange(Number(e.target.value))}
                disabled={loadingList}
              >
                {loadingList ? (
                  <MenuItem value="" disabled>Loading…</MenuItem>
                ) : (
                  cecNumbers.map((n) => (
                    <MenuItem key={n.id} value={n.id}>
                      {n.cec_number}
                    </MenuItem>
                  ))
                )}
              </Select>
            </FormControl>

            {/* Save button — visible only when data is loaded */}
            {cecData && (
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            )}
          </Box>
        </Paper>

        {/* ── States ── */}
        {!selectedId && !loadingList && (
          <Alert severity="info" sx={{ borderRadius: 2 }}>
            Select a CEC number above to view and edit its parameters.
          </Alert>
        )}

        {loadingData && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        )}

        {/* ── Parameter table ── */}
        {cecData && !loadingData && (
          <Paper
            elevation={0}
            sx={(theme) => ({
              border: `1px solid ${theme.palette.divider}`,
              borderRadius: 2,
              overflow: 'hidden',
            })}
          >
            <TableContainer>
              <Table size="small">
                <AppTableHead>
                  <TableRow>
                    <AppTableHeadCell sx={{ width: '40%' }}>Parameter</AppTableHeadCell>
                    <AppTableHeadCell sx={{ width: '30%' }}>Desired Min</AppTableHeadCell>
                    <AppTableHeadCell sx={{ width: '30%' }}>Desired</AppTableHeadCell>
                  </TableRow>
                </AppTableHead>
                <TableBody>
                  {PARAMETERS.map((param) => (
                    <AppTableRow key={param.key}>
                      {/* Parameter label */}
                      <TableCell>
                        <Typography variant="body2" fontWeight={500}>
                          {param.label}
                        </Typography>
                      </TableCell>

                      {/* Desired Min input */}
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ step: param.integer ? 1 : 'any' }}
                          value={desiredMin[param.key] ?? ''}
                          onChange={(e) =>
                            setDesiredMin((prev) => ({ ...prev, [param.key]: e.target.value }))
                          }
                          sx={{ width: 120 }}
                        />
                      </TableCell>

                      {/* Desired input */}
                      <TableCell>
                        <TextField
                          size="small"
                          type="number"
                          inputProps={{ step: param.integer ? 1 : 'any' }}
                          value={desired[param.key] ?? ''}
                          onChange={(e) =>
                            setDesired((prev) => ({ ...prev, [param.key]: e.target.value }))
                          }
                          sx={{ width: 120 }}
                        />
                      </TableCell>
                    </AppTableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>

            {/* Bottom save bar */}
            <Box
              sx={(theme) => ({
                display: 'flex',
                justifyContent: 'flex-end',
                px: 2,
                py: 1.5,
                borderTop: `1px solid ${theme.palette.divider}`,
                background: alpha(theme.palette.background.paper, 0.6),
              })}
            >
              <Button
                variant="contained"
                startIcon={saving ? <CircularProgress size={16} color="inherit" /> : <SaveIcon />}
                onClick={handleSave}
                disabled={saving}
              >
                {saving ? 'Saving…' : 'Save'}
              </Button>
            </Box>
          </Paper>
        )}
      </Container>

      {/* Snackbar feedback */}
      <Snackbar
        open={snack.open}
        autoHideDuration={4000}
        onClose={() => setSnack((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          severity={snack.severity}
          variant="filled"
          onClose={() => setSnack((s) => ({ ...s, open: false }))}
          sx={{ width: '100%' }}
        >
          {snack.msg}
        </Alert>
      </Snackbar>
    </>
  );
}
